import { useQuery } from 'react-query'
import { fetcher } from 'src/fetcher'
import { BASE_API_URL } from 'src/globals'
import { useUserInfo } from 'src/hooks'
import { AppointmentProfile, CareType } from 'src/types'
import { getOfferingsParams, IGetOfferingsParams } from 'src/utils'

export async function getCareTypes(): Promise<CareType[]> {
    return await fetcher<CareType[]>(
        `${BASE_API_URL}/tmd-api/appointments/care-types?apiVersion=v2`
    )
}

export const useCareTypes = (name?: string) => {
    const { data, isLoading } = useQuery<CareType[]>(
        ['care-types'],
        getCareTypes,
        {
            staleTime: Infinity // data unlikely to change during a session
        }
    )

    // Filtering here instead of in the query
    // takes advantage of API request caching
    if (name) {
        return {
            careType: (data || []).find(careType => careType.name === name),
            isLoading
        }
    } else {
        return {
            careTypes: data || [],
            isLoading
        }
    }
}

async function getAppointmentProfiles(
    params: IGetOfferingsParams
): Promise<AppointmentProfile[]> {
    const profiles = await fetcher<AppointmentProfile[]>(
        `${BASE_API_URL}/schedule-api/api/v1/offerings${getOfferingsParams(
            params
        )}`
    )

    return profiles.sort((a: AppointmentProfile, b: AppointmentProfile) => {
        return a.label < b.label ? -1 : 1
    })
}

export const useAppointmentProfilesByCareTypeName = (careTypeName: string) => {
    // We no longer support filtering by care types. This is a temporary solution
    const { user, isLoading: userIsLoading } = useUserInfo()
    const { data, isLoading } = useQuery<AppointmentProfile[]>(
        [`appointment-profiles-by-care-type-name`, careTypeName, user?.id],
        () =>
            getAppointmentProfiles({
                patientId: user?.id
            }),
        {
            staleTime: 1000 * 60 * 5,
            enabled: !!user?.id
        }
    )

    return {
        appointmentProfiles: data || [],
        isLoading: isLoading || userIsLoading
    }
}

export const useAppointmentProfilesByApptProfileName = (
    apptProfileName: string,
    automatedSuggestion = false
) => {
    const { user } = useUserInfo()
    const { data, isLoading } = useQuery<AppointmentProfile[]>(
        [`appointment-profiles-by-appt-profile-name`, apptProfileName],
        () =>
            getAppointmentProfiles({
                patientId: user?.id,
                profileNames: [apptProfileName],
                automatedSuggestion
            }),
        {
            staleTime: 1000 * 60 * 5 // refetch rendered data after 5min
        }
    )

    return {
        appointmentProfiles: data || [],
        isLoading
    }
}

export const useCareTypeViewData = (careTypeName: string) => {
    // Make both requests in parallel
    const careTypeMeta = useCareTypes(careTypeName)
    const profiles = useAppointmentProfilesByCareTypeName(careTypeName)

    return {
        careType: careTypeMeta.careType,
        appointmentProfiles: profiles.appointmentProfiles,
        isLoading: careTypeMeta.isLoading || profiles.isLoading
    }
}
